import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import NavigationBar from '@ui-parts/NavigationBar';
import Footer from '@ui-parts/Footer';

import useHandleLogout from '@functional/useHandleLogout';
import { isAdminPath } from '@functional';

import { HEADER } from '~/assets/styles/base';
import { ROUTES } from '~/constants/routes';
import { HTTP_STATUS } from '~/constants/api';

const StyledBody = styled.div`
  min-height: 100vh;
  position: relative;
  padding-bottom: 60px;
  box-sizing: border-box;
`;

interface ErrorPageWrapperProps {
  children: React.ReactNode;
}

const ErrorPageWrapper: React.FC<ErrorPageWrapperProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const axiosError = useSelector((state: any) => state.axiosError);

  const handleLogout = useHandleLogout({
    isAdmin: isAdminPath(),
  });

  // locationチェンジでスクロール位置をページ上部に移動させる
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    // axiosErrorをクリアしないとリダイレクトループしてしまう可能性があるので、ここでクリアしてあげる。
    dispatch({ type: 'axiosError/initializeAxiosError' });
  }, []);

  /**
   * セッション切れでログアウトボタンクリック時に401エラーが出る場合を考慮。
   * 401の場合にログイン画面に遷移する。
   */
  useEffect(() => {
    if (axiosError === null) {
      return;
    }

    const { status } = axiosError;
    if (status) {
      switch (status) {
        // 401の場合にログイン画面に遷移する。
        case HTTP_STATUS.UNAUTHORIZED:
          if (isAdminPath()) {
            // 401のときはuserSliceを初期化
            dispatch({ type: 'admin/unauthorizedErrorRaised' });
            navigate(ROUTES.ADMIN.SIGNIN);
            break;
          }
          // 401のときはuserSliceを初期化
          dispatch({ type: 'user/unauthorizedErrorRaised' });
          navigate(ROUTES.USER.SIGNIN);
          break;

        // それ以外は無視
        default:
          break;
      }
    }
  }, [axiosError]);

  return (
    <StyledBody>
      <NavigationBar
        height={HEADER.navigationBar.height}
        isHiddenNavButtons
        isHiddenLogoutButton
        handleLogout={handleLogout}
        isAdmin={isAdminPath()}
        bgColor={
          isAdminPath() ? HEADER.navigationBar.bgColor.admin : HEADER.navigationBar.bgColor.user
        }
      />
      {children}
      <Footer />
    </StyledBody>
  );
};

ErrorPageWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorPageWrapper;
